import { type AxiosInstance } from "axios";
import {
  type Acceptance,
  type CandidatePage,
  type InputRow,
  type Mapping,
  type MatchResponse,
  type ParsedFile,
  type Settings,
} from "../corporationList/types";

export const CORPORATION_LIST_ENDPOINT =
  "/commercial-picture-request/corporation-list-";

/** 法人リストの受付可否・上限・取得単価を取得する。 */
export const getCorporationListSettings = async (
  client: AxiosInstance,
  signal: AbortSignal
): Promise<Settings> => {
  const { data } = await client.get<Settings>(
    CORPORATION_LIST_ENDPOINT + "settings/",
    { signal }
  );
  return data;
};

/** CSV・Excelを解析し、列と入力行を取得する。 */
export const parseCorporationListFile = async (
  client: AxiosInstance,
  file: File,
  signal: AbortSignal
): Promise<ParsedFile> => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await client.post<ParsedFile>(
    CORPORATION_LIST_ENDPOINT + "files/",
    body,
    { signal }
  );
  return data;
};

/** 列の対応と入力行を使って法人を照合する。 */
export const matchCorporationList = async (
  client: AxiosInstance,
  columnMapping: Mapping,
  rows: InputRow[],
  signal: AbortSignal
): Promise<MatchResponse> => {
  const { data } = await client.post<MatchResponse>(
    CORPORATION_LIST_ENDPOINT + "matches/",
    { columnMapping, rows },
    { signal }
  );
  return data;
};

/** ダウンロードと画面上での試用に共通のサンプルを取得する。 */
export const getCorporationListSample = async (
  client: AxiosInstance,
  fileType: "csv" | "xlsx",
  signal?: AbortSignal
): Promise<Blob> => {
  const { data } = await client.get<Blob>(
    CORPORATION_LIST_ENDPOINT + "samples/",
    {
      params: { fileType },
      responseType: "blob",
      ...(signal ? { signal } : {}),
    }
  );
  return data;
};

/** 法人の候補を取得する。初回はページ送りのカーソルを送らない。 */
export const getCorporationListCandidates = async (
  client: AxiosInstance,
  input: Pick<
    InputRow,
    "companyName" | "prefectureName" | "cityName" | "townName"
  >,
  after: string,
  signal: AbortSignal
): Promise<CandidatePage> => {
  const { data } = await client.get<CandidatePage>(
    CORPORATION_LIST_ENDPOINT + "candidates/",
    {
      params: {
        companyName: input.companyName,
        prefectureName: input.prefectureName,
        cityName: input.cityName,
        townName: input.townName,
        ...(after ? { afterCorporateNumber: after } : {}),
      },
      signal,
    }
  );
  return data;
};

/** 応答を受け取れなかった依頼の受付を同じキーで照会する。 */
export const getCorporationListAcceptance = async (
  client: AxiosInstance,
  idempotencyKey: string
): Promise<Acceptance> => {
  const { data } = await client.get<Acceptance>(
    CORPORATION_LIST_ENDPOINT + `requests/${idempotencyKey}/`
  );
  return data;
};

/** 確認済みの法人について、全部事項・現在の登記の取得を依頼する。 */
export const requestCorporationList = async (
  client: AxiosInstance,
  idempotencyKey: string,
  expectedUnitPrice: number,
  targets: Array<{ rowNumber: number; corporateIdentificationNumber: string }>
): Promise<Acceptance> => {
  const { data } = await client.post<Acceptance>(
    CORPORATION_LIST_ENDPOINT + "requests/",
    {
      idempotencyKey,
      expectedUnitPrice,
      targets,
      documentType: "ALL_MATTERS",
      closed: false,
      withCommercialPictureAnalyze: true,
    }
  );
  return data;
};
